// API service with mock implementation for development
import { TradingSignal, CapeScore, FScore, PnFChart, FinancialData, PnFColumn } from './types';

// Mock data for development
const mockTradingSignals: TradingSignal[] = [
  {
    ticker: 'AAPL',
    cape: 30.5,
    capePercentile: 85.2,
    f_score: 8,
    preliminarySignal: 'Hold',
    finalSignal: 'Buy',
    date: new Date().toISOString().split('T')[0]
  },
  {
    ticker: 'MSFT',
    cape: 28.7,
    capePercentile: 78.3,
    f_score: 7,
    preliminarySignal: 'Hold',
    finalSignal: 'Buy',
    date: new Date().toISOString().split('T')[0]
  }
];

// Mock S&P 500 tickers for development
const mockSP500Tickers = [
  { ticker: 'AAPL', name: 'Apple Inc.' },
  { ticker: 'MSFT', name: 'Microsoft Corporation' },
  { ticker: 'AMZN', name: 'Amazon.com Inc.' },
  { ticker: 'GOOGL', name: 'Alphabet Inc.' },
  { ticker: 'META', name: 'Meta Platforms Inc.' },
  { ticker: 'TSLA', name: 'Tesla Inc.' },
  { ticker: 'NVDA', name: 'NVIDIA Corporation' },
  { ticker: 'JPM', name: 'JPMorgan Chase & Co.' },
  { ticker: 'V', name: 'Visa Inc.' },
  { ticker: 'PG', name: 'Procter & Gamble Co.' }
];

// Mock stock analysis data
const mockStockAnalysis = {
  AAPL: {
    capeScore: {
      cape: 30.5,
      capePercentile: 0.85,
      date: new Date().toISOString().split('T')[0]
    } as CapeScore,
    fScore: {
      score: 8,
      roa: true,
      cfo: true,
      deltaRoa: true,
      accrual: true,
      deltaLeverage: true,
      deltaLiquidity: true,
      noEquityRaise: true,
      deltaMargin: false,
      deltaTurnover: true
    } as FScore,
    pnfChart: {
      ticker: 'AAPL',
      columns: [
        { direction: 'up', boxes: [170, 171, 172, 173, 174, 175], high: 175, low: 170 }
      ],
      patterns: {
        tripleTopBreakout: true,
        tripleTopBreakdown: false,
        quadTopBreakout: false,
        quadTopBreakdown: false
      },
      currentPrice: 175.80
    } as PnFChart,
    financialData: {
      ticker: 'AAPL',
      priceHistory: Array.from({ length: 30 }, (_, i) => ({
        date: new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        open: 175 + Math.random() * 5,
        high: 180 + Math.random() * 5,
        low: 170 + Math.random() * 5,
        close: 176 + Math.random() * 4,
        volume: 70000000 + Math.random() * 10000000
      })),
      incomeStatement: [
        { 
          date: '2023-01-01', 
          totalRevenue: 394328000000, 
          grossProfit: 173973000000, 
          netIncome: 96995000000,
          eps: 6.14
        },
        { 
          date: '2022-01-01', 
          totalRevenue: 394328000000, 
          grossProfit: 170782000000, 
          netIncome: 99803000000,
          eps: 6.11
        },
        { 
          date: '2021-01-01', 
          totalRevenue: 365817000000, 
          grossProfit: 152836000000, 
          netIncome: 94680000000,
          eps: 5.61
        }
      ],
      balanceSheet: [],
      cashFlow: []
    } as FinancialData
  },
  MSFT: {
    capeScore: {
      cape: 28.7,
      capePercentile: 0.78,
      date: new Date().toISOString().split('T')[0]
    } as CapeScore,
    fScore: {
      score: 7,
      roa: true,
      cfo: true,
      deltaRoa: false,
      accrual: true,
      deltaLeverage: true,
      deltaLiquidity: true,
      noEquityRaise: true,
      deltaMargin: true,
      deltaTurnover: false
    } as FScore,
    pnfChart: {
      ticker: 'MSFT',
      columns: [
        { direction: 'up', boxes: [310, 311, 312, 313, 314, 315], high: 315, low: 310 }
      ],
      patterns: {
        tripleTopBreakout: false,
        tripleTopBreakdown: false,
        quadTopBreakout: true,
        quadTopBreakdown: false
      },
      currentPrice: 315.75
    } as PnFChart,
    financialData: {
      ticker: 'MSFT',
      priceHistory: Array.from({ length: 30 }, (_, i) => ({
        date: new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        open: 310 + Math.random() * 5,
        high: 320 + Math.random() * 5,
        low: 305 + Math.random() * 5,
        close: 315 + Math.random() * 4,
        volume: 30000000 + Math.random() * 5000000
      })),
      incomeStatement: [
        { 
          date: '2023-01-01', 
          totalRevenue: 211915000000, 
          grossProfit: 147060000000, 
          netIncome: 72361000000,
          eps: 9.68
        },
        { 
          date: '2022-01-01', 
          totalRevenue: 198270000000, 
          grossProfit: 135620000000, 
          netIncome: 67450000000,
          eps: 8.94
        },
        { 
          date: '2021-01-01', 
          totalRevenue: 168088000000, 
          grossProfit: 115856000000, 
          netIncome: 61271000000,
          eps: 8.05
        }
      ],
      balanceSheet: [],
      cashFlow: []
    } as FinancialData
  }
};

// Default stock data for any ticker not in our mock data
const defaultStockAnalysis = {
  capeScore: {
    cape: 25.0,
    capePercentile: 0.5,
    date: new Date().toISOString().split('T')[0]
  } as CapeScore,
  fScore: {
    score: 5,
    roa: true,
    cfo: true,
    deltaRoa: false,
    accrual: true,
    deltaLeverage: false,
    deltaLiquidity: false,
    noEquityRaise: true,
    deltaMargin: false,
    deltaTurnover: true
  } as FScore,
  pnfChart: {
    ticker: '',
    columns: [
      { direction: 'up', boxes: [100, 101, 102, 103], high: 103, low: 100 }
    ],
    patterns: {
      tripleTopBreakout: false,
      tripleTopBreakdown: false,
      quadTopBreakout: false,
      quadTopBreakdown: false
    },
    currentPrice: 100.00
  } as PnFChart,
  financialData: {
    ticker: '',
    priceHistory: Array.from({ length: 30 }, (_, i) => ({
      date: new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      open: 100 + Math.random() * 5,
      high: 105 + Math.random() * 5,
      low: 95 + Math.random() * 5,
      close: 101 + Math.random() * 4,
      volume: 10000000 + Math.random() * 5000000
    })),
    incomeStatement: [
      { 
        date: '2023-01-01', 
        totalRevenue: 100000000, 
        grossProfit: 70000000, 
        netIncome: 30000000,
        eps: 3.00
      },
      { 
        date: '2022-01-01', 
        totalRevenue: 95000000, 
        grossProfit: 65000000, 
        netIncome: 28000000,
        eps: 2.80
      },
      { 
        date: '2021-01-01', 
        totalRevenue: 90000000, 
        grossProfit: 60000000, 
        netIncome: 25000000,
        eps: 2.50
      }
    ],
    balanceSheet: [],
    cashFlow: []
  } as FinancialData
};

// Mock price data for portfolios
const mockPrices = {
  AAPL: 175.80,
  MSFT: 315.75,
  AMZN: 147.42,
  GOOGL: 131.86,
  META: 325.11,
  TSLA: 251.05,
  NVDA: 487.15,
  JPM: 183.99,
  V: 269.04,
  PG: 162.53
};

// API Service interface
interface ApiService {
  runStrategy: (params: { ticker: string }) => Promise<TradingSignal[]>;
  getTradingSignals: () => Promise<TradingSignal[]>;
  getSP500Tickers: () => Promise<Array<{ ticker: string; name: string }>>;
  getStockAnalysis: (ticker: string) => Promise<{
    capeScore: CapeScore;
    fScore: FScore;
    pnfChart: PnFChart;
    financialData: FinancialData;
  }>;
  getCurrentPrice: (ticker: string) => Promise<number>;
  getPortfolioPositions: () => Promise<any[]>;
  addPosition: (position: any) => Promise<any>;
  updatePosition: (id: string, position: any) => Promise<any>;
  removePosition: (id: string) => Promise<void>;
  updateCashBalance: (balance: number) => Promise<number>;
  getPortfolio: () => Promise<any>;
  refreshPositionPrices: () => Promise<any[]>;
}

// Implementation with mock data for now
export const apiService: ApiService = {
  runStrategy: async (params) => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    return mockTradingSignals;
  },
  
  getTradingSignals: async () => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    return mockTradingSignals;
  },
  
  getSP500Tickers: async () => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    return mockSP500Tickers;
  },
  
  getStockAnalysis: async (ticker) => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Return mock data for the requested ticker, or default if not found
    const analysis = mockStockAnalysis[ticker as keyof typeof mockStockAnalysis] || {
      ...defaultStockAnalysis,
      financialData: {
        ...defaultStockAnalysis.financialData,
        ticker
      }
    };
    
    return analysis;
  },
  
  getCurrentPrice: async (ticker) => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500));
    return mockPrices[ticker as keyof typeof mockPrices] || 100.0;
  },
  
  // Portfolio methods
  getPortfolioPositions: async () => {
    await new Promise(resolve => setTimeout(resolve, 500));
    return [];
  },
  
  addPosition: async (position) => {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      id: Date.now().toString(),
      ...position,
      created_at: new Date().toISOString()
    };
  },
  
  updatePosition: async (id, position) => {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      ...position,
      updated_at: new Date().toISOString()
    };
  },
  
  removePosition: async (id) => {
    await new Promise(resolve => setTimeout(resolve, 500));
  },
  
  updateCashBalance: async (balance) => {
    await new Promise(resolve => setTimeout(resolve, 500));
    return balance;
  },
  
  getPortfolio: async () => {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      cash_balance: 100000,
      positions: []
    };
  },
  
  refreshPositionPrices: async () => {
    await new Promise(resolve => setTimeout(resolve, 500));
    return [];
  }
}; 